import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Phone, Sparkles } from "lucide-react";
import { AddressPicker, addressError } from "@/components/pawkt/AddressPicker";
import { IMG, Title, Eyebrow, Btn, Nav, Field, Choice, PetAvatar } from "./ui";

export function Welcome({ onStart, business }) {
  const reduce = useReducedMotion();
  return (
    <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr]" data-testid="sb-welcome">
      <div className="order-2 lg:order-1">
        <Eyebrow>{business || "Welcome"}</Eyebrow>
        <h1 className="sb-serif mt-4 text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" data-testid="sb-welcome-title">
          A little more love for <em className="text-[var(--sb-plum)]">your best friend.</em>
        </h1>
        <p className="mt-6 max-w-md text-lg text-[var(--sb-muted)]">Let&rsquo;s make their next visit special.</p>
        <Btn onClick={onStart} testid="sb-start" className="mt-10 px-10 text-lg">Let&rsquo;s get started <ArrowRight className="h-5 w-5" /></Btn>
      </div>
      <motion.div className="sb-grain relative order-1 overflow-hidden rounded-[2.5rem] lg:order-2"
        initial={reduce ? false : { opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: "easeOut" }}>
        <img src={IMG.hero} alt="A happy golden retriever" className="aspect-[4/3] w-full object-cover lg:aspect-[4/5]" />
        <div className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-sm font-semibold text-[var(--sb-plum)] backdrop-blur-xl">
          <Sparkles className="h-4 w-4" /> Boutique care, every visit
        </div>
      </motion.div>
    </div>
  );
}

export function Mobile({ value, onChange, onNext, busy, error }) {
  const ok = /^\d{10}$/.test(value);
  return (
    <div data-testid="sb-step-mobile">
      <Eyebrow>Step 1</Eyebrow>
      <div className="mt-3"><Title sub="We'll use it to find your details, or set you up in a moment.">What&rsquo;s your mobile number?</Title></div>
      <form className="mt-10 max-w-md" onSubmit={(e) => { e.preventDefault(); if (ok) onNext(); }}>
        <Field label="Mobile number" error={error || (value.length >= 10 && !ok ? "Please enter a 10-digit mobile number." : "")}>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--sb-lav)]" />
            <input autoFocus inputMode="numeric" autoComplete="off" value={value} data-testid="sb-mobile-input"
              onChange={(e) => onChange(e.target.value.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "").slice(0, 10))} placeholder="98765 43210"
              className="sb-input sb-input--icon sb-input--mobile" />
          </div>
        </Field>
        <Nav onNext={onNext} disabled={!ok} busy={busy} />
      </form>
    </div>
  );
}

export function Confirm({ matches, onPick, onNotMe, onBack }) {
  return (
    <div data-testid="sb-step-confirm">
      <Eyebrow>Lovely to see you</Eyebrow>
      <div className="mt-3"><Title sub="Tap your name to continue.">Welcome back! Is this you?</Title></div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {matches.map((c) => (
          <Choice key={c.id} onClick={() => onPick(c)} testid={`sb-match-${c.id}`} className="p-6">
            <p className="sb-serif break-words pr-10 text-3xl font-semibold">{c.first_name || "Welcome"}</p>
            <p className="mt-1 text-sm text-[var(--sb-muted)]">Mobile {c.mobile_masked}</p>
            {c.pets.length > 0 && (
              <div className="mt-5 flex -space-x-3">{c.pets.slice(0, 4).map((p) => <PetAvatar key={p.id} pet={p} size="h-12 w-12 ring-4 ring-white" />)}</div>
            )}
          </Choice>
        ))}
      </div>
      <button type="button" onClick={onNotMe} className="mt-6 text-base font-semibold text-[var(--sb-plum)] underline-offset-4 hover:underline" data-testid="sb-not-me">That&rsquo;s not me</button>
      <Nav onBack={onBack} />
    </div>
  );
}

export function NotMe({ onBack }) {
  return (
    <div data-testid="sb-step-notme">
      <Title sub="This number is already linked to a profile. Our reception team will gladly help you check in.">Let&rsquo;s get you to the right place</Title>
      <Nav onBack={onBack} />
    </div>
  );
}

export function Details({ isNew, need, value, onChange, onNext, onBack }) {
  const [tried, setTried] = useState(false);
  const askName = isNew || need.includes("name");
  const askAddr = isNew || need.some((f) => ["pin", "state", "city", "area", "country"].includes(f));
  const nameErr = askName && !value.name.trim() ? "Please tell us your name." : "";
  const addrErr = askAddr ? addressError(value) : "";
  const go = () => { setTried(true); if (!nameErr && !addrErr) onNext(); };
  return (
    <div data-testid="sb-step-details">
      <Eyebrow>{isNew ? "Nice to meet you" : "Just one more thing"}</Eyebrow>
      <div className="mt-3"><Title sub={isNew ? "A few details so we can welcome you properly." : "We only need what's missing from your profile."}>
        {isNew ? "Tell us a little about you" : "Help us complete your profile"}</Title></div>
      <div className="mt-10 grid max-w-2xl gap-6">
        {askName && <Field label="Your full name" error={tried ? nameErr : ""}>
          <input className="sb-input" value={value.name} maxLength={80} onChange={(e) => onChange({ name: e.target.value })} data-testid="sb-name-input" placeholder="e.g. Ananya Rao" />
        </Field>}
        {askAddr && <div className="sb-addr"><AddressPicker value={value} onChange={onChange} error={tried ? addrErr : ""} testid="sb-addr" /></div>}
      </div>
      <Nav onBack={onBack} onNext={go} />
    </div>
  );
}
